import { Injectable, inject } from '@angular/core';

import { WhatsAppDeviceSdk } from '@libs/graphql/sdk/whats-app-device/whats-app-device.sdk';
import { WhatsAppSendLogSdk } from '@libs/graphql/sdk/whats-app-send-log/whats-app-send-log.sdk';
import type { WhatsAppDevicePoco, WhatsAppSendLogPoco } from '@libs/graphql/models';
import {
  WhatsAppDeviceService, eventStatusOf, isLiveInstance, recipientStatusOf,
} from '@modules/admin/whatsapp/device/whatsapp-device.service';
import type { SendResult } from '@modules/admin/whatsapp/device/whatsapp-device.service';
import { buildAuditFields, newEntityGuid } from '@shared/utils/audit-fields';
import { localNowForDb } from '@shared/utils/local-time';
import { environment } from '../../../environments/environment';

/* ═══════════ Transactional WhatsApp notifications ═══════════
   The admin WhatsApp page BROADCASTS a template to a chosen audience. This service is the other
   half: a single message fired by a booking event (a vendor proposing a new event date, the
   customer accepting it, …) to the one person the event concerns.

   Both halves share the same plumbing — the claimed WhatsAppDevice row picks the connector,
   WhatsAppDeviceService.sendMessage talks to Chatvion through the API proxy, and every send is
   logged to WhatsAppSendLog so it shows up in the admin History tab.

   BEST-EFFORT BY DESIGN: `notify()` never throws. A booking write must not be rolled back (or
   reported as failed) because no device happens to be linked — the proposal itself is the record
   of the change, WhatsApp is only how the other party hears about it sooner. Callers that want to
   tell the user get the boolean back. */

export interface WaNotifyRequest {
  /** Stable code for the History tab's filter/label (e.g. `booking_date_change_proposed`). */
  templateCode: string;
  /** Human label shown in the History list. */
  templateLabel: string;
  audience: 'Customer' | 'Vendor';
  /** Recipient's display name — logged with the send so History names the person. */
  name: string;
  phone?: string | null;
  body: string;
  /** Who to stamp on the log row's audit columns. */
  userId: number;
}

/**
 * Dump every outgoing message body to the browser console (DevTools → Console, filter `[WA]`).
 * Added while the Chatvion device could not deliver, so a message that never leaves is still
 * readable and testable.
 *
 * Tied to the BUILD, not a hand-flipped boolean. A `true` left in by mistake would print customer
 * names, phone numbers and booking details into the console of every production session — so it
 * is off for anything deployed (`qc` and `production` both set `production: true`) and on for a
 * local `ng serve`.
 *
 * Note the guarded code is still PRESENT in the released bundle, just never reached: esbuild
 * cannot constant-fold a property read across the module boundary, so it emits
 * `var x = !environment.production` rather than a literal `false`. Verified against a production
 * build — the flag resolves to false and neither `console.info` runs. It is inert, not absent.
 */
const WA_DEBUG_TO_CONSOLE = !environment.production;

@Injectable({ providedIn: 'root' })
export class WhatsAppNotifyService {
  private readonly device = inject(WhatsAppDeviceService);
  private readonly deviceSdk = inject(WhatsAppDeviceSdk);
  private readonly sendLogSdk = inject(WhatsAppSendLogSdk);

  /** The live connector id (null = none), and when it was resolved. Re-resolved after
   *  CONNECTOR_TTL_MS: a once-per-page-load cache kept a long-open tab sending through a device that
   *  had since been unlinked (QA 10-30: "Unknown instanceId", logged as sent). */
  private connectorId: string | null | undefined;
  private connectorAt = 0;
  private static readonly CONNECTOR_TTL_MS = 60_000;

  /**
   * Send one transactional message and log it. Returns true when Chatvion accepted it; false for
   * every failure mode (no phone on file, no device linked, connector refused, network error).
   */
  async notify(req: WaNotifyRequest): Promise<boolean> {
    const phone = (req.phone ?? '').trim();
    this.debugDump(req, phone);
    if (!phone) return false;
    try {
      const instanceId = await this.resolveConnector();
      // A null instanceId still sends — Chatvion falls back to the tenant's default device — so
      // try it either way, then retry on the default if the claimed connector refused (the same
      // two-step the History tab's resend uses).
      let res = await this.device.sendMessage(instanceId, phone, req.body);
      if (!res.accepted && instanceId) res = await this.device.sendMessage(null, phone, req.body);
      if (!res.accepted) this.connectorId = undefined;   // next send re-resolves the device
      if (WA_DEBUG_TO_CONSOLE) {
        console.info(`%c[WA] ${res.accepted ? 'ACCEPTED' : 'NOT SENT'}%c  ${req.templateCode}`
          + `  ·  device: ${instanceId ?? '(none — tenant default)'}`
          + (res.error ? `  ·  error: ${res.error}` : ''),
          `font-weight:700;color:${res.accepted ? '#0B7C8C' : '#c0392b'}`, 'color:inherit');
      }
      await this.log(req, phone, instanceId, res);
      return res.accepted;
    } catch (err) {
      console.warn('WhatsApp notify failed', err);
      return false;
    }
  }

  /* ── TEMPORARY (testing) ─────────────────────────────────────────────────────
     Chatvion cannot deliver right now, so every outgoing message is dumped to the browser
     console instead of only existing inside a request nobody can read. DevTools → Console,
     filter on `[WA]` to see exactly what each booking/proposal would have sent.

     Delete this method, the `WA_DEBUG_TO_CONSOLE` flag and its two call sites once the device
     is connected again — the permanent record is WhatsAppSendLog (admin → WhatsApp → History),
     which is written either way. */
  private debugDump(req: WaNotifyRequest, phone: string): void {
    if (!WA_DEBUG_TO_CONSOLE) return;
    console.info(
      `%c[WA] ${req.templateCode}%c → ${phone || '(NO PHONE — nothing will be sent)'}`
      + `  ·  ${req.audience}: ${req.name || '(unnamed)'}`
      + `\n%c${req.body}`,
      'font-weight:700;color:#0B7C8C', 'color:inherit',
      'color:#334; white-space:pre-wrap',
    );
  }

  /** Which device to send through: the newest claimed WhatsAppDevice row whose device Chatvion
   *  reports live (CONNECTED with a WhatsApp account — isLiveInstance). Re-linking can leave several
   *  claimed rows behind, and the newest is not always the one that works. Cached for a minute. */
  private async resolveConnector(): Promise<string | null> {
    if (this.connectorId !== undefined && Date.now() - this.connectorAt < WhatsAppNotifyService.CONNECTOR_TTL_MS) {
      return this.connectorId;
    }
    this.connectorId = null;
    this.connectorAt = Date.now();
    try {
      const res = await this.deviceSdk.list(
        { isDeleted: false }, [{ column: 'id', desc: true }],
        ['id', 'instanceId', 'status', 'isDeleted'], 1, 20);
      const claimed = ((res.data as WhatsAppDevicePoco[]) ?? []).map(d => d.instanceId).filter((x): x is string => !!x);
      if (!claimed.length) return this.connectorId;
      const live = (await this.device.listInstances()).instances ?? [];
      this.connectorId = claimed.find(id => isLiveInstance(live.find(i => i.instanceId === id))) ?? null;
    } catch { this.connectorId = null; }
    return this.connectorId;
  }

  /** Record the send in WhatsAppSendLog so it appears in the admin History tab (best-effort —
   *  a missing/undeployed table must not turn a delivered message into a reported failure). */
  private async log(
    req: WaNotifyRequest, phone: string, instanceId: string | null, res: SendResult,
  ): Promise<void> {
    try {
      // QUEUED, not SENT, until Chatvion confirms delivery — History re-checks it by logId. Logging an
      // accepted-but-queued message as "Sent" is what hid six months of undelivered messages (10-30).
      const rs = recipientStatusOf(res);
      const payload: WhatsAppSendLogPoco = {
        id: 0, guid: newEntityGuid(),
        templateCode: req.templateCode,
        templateLabel: req.templateLabel,
        audience: req.audience,
        body: req.body,
        recipientsJson: JSON.stringify([{
          phone, name: req.name, status: rs, error: res.error ?? '', logId: res.logId ?? '',
        }]),
        recipientCount: 1,
        sentCount: rs === 'SENT' ? 1 : 0,
        status: eventStatusOf([rs]),
        instanceId: instanceId ?? undefined,
        sentDate: localNowForDb(),
        ...buildAuditFields(req.userId),
      };
      await this.sendLogSdk.save(payload);
    } catch { /* log-only failure — the message itself already went out */ }
  }
}
